import type {
  BigCommerceMerchantOnboarding,
  BigCommerceOnboardingStep,
  BigCommerceOnboardingStepKey,
  OnboardingStepStatus,
} from '../../../../store/modules/merchant/types';
import { bigcommerceOnboardingStepKeys } from '../../../../store/modules/merchant/types';
import {
  colorDisabled,
  colorError,
  colorSuccess,
  colorWarning,
  type StatusColor,
} from '../../../../util/constants';

/** Prod BigCommerce marketplace listing — single app for all merchants. */
export const BIGCOMMERCE_APP_INSTALL_URL = 'https://www.bigcommerce.com/apps/skipify/';

export type ProvisioningStatus = {
  label: string;
  color: StatusColor;
  needsAction: boolean;
  reason?: string;
};

export const BIGCOMMERCE_STEP_LABELS: Record<BigCommerceOnboardingStepKey, string> = {
  store_registered: 'Store registered',
  oauth_complete: 'Install Skipify app',
  scripts_injected: 'Checkout script injected',
  setup_verified: 'Verify storefront setup',
};

export const ONBOARDING_STATUS_LABELS: Record<OnboardingStepStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  done: 'Done',
  blocked: 'Blocked',
  failed: 'Failed',
};

export const ONBOARDING_STATUS_COLORS: Record<OnboardingStepStatus, StatusColor> = {
  pending: colorDisabled,
  in_progress: colorWarning,
  done: colorSuccess,
  blocked: colorWarning,
  failed: colorError,
};

export const getStepLabel = (key: BigCommerceOnboardingStepKey): string =>
  BIGCOMMERCE_STEP_LABELS[key] ?? key;

export const getStatusLabel = (status: OnboardingStepStatus): string =>
  ONBOARDING_STATUS_LABELS[status];

export const getStep = (
  onboarding: BigCommerceMerchantOnboarding | undefined,
  key: BigCommerceOnboardingStepKey,
) => onboarding?.steps.find((step) => step.key === key);

export const isOnboardingComplete = (onboarding: BigCommerceMerchantOnboarding): boolean =>
  onboarding.steps.length > 0 && onboarding.steps.every((step) => step.status === 'done');

export const MERCHANT_OWNED_STEP_KEYS: readonly BigCommerceOnboardingStepKey[] =
  bigcommerceOnboardingStepKeys.filter(
    (key) => key !== 'store_registered' && key !== 'setup_verified',
  );

export const getMerchantOwnedSteps = (
  onboarding: BigCommerceMerchantOnboarding,
): BigCommerceOnboardingStep[] =>
  onboarding.steps.filter((step) =>
    MERCHANT_OWNED_STEP_KEYS.includes(step.key as BigCommerceOnboardingStepKey),
  );

export const getMerchantProvisioningStatus = (
  onboarding: BigCommerceMerchantOnboarding,
): ProvisioningStatus => {
  const merchantSteps = getMerchantOwnedSteps(onboarding);

  if (merchantSteps.length > 0 && merchantSteps.every((step) => step.status === 'done')) {
    return { label: 'Complete', color: colorSuccess, needsAction: false };
  }

  const active = merchantSteps.find(
    (step) =>
      step.status === 'in_progress' || step.status === 'failed' || step.status === 'blocked',
  );
  if (active?.status === 'failed') {
    return {
      label: 'Failed',
      color: colorError,
      needsAction: true,
      reason: `${getStepLabel(active.key)} failed.`,
    };
  }
  if (active?.status === 'blocked') {
    return {
      label: 'Blocked',
      color: colorError,
      needsAction: true,
      reason: `${getStepLabel(active.key)} is blocked.`,
    };
  }
  if (active?.status === 'in_progress') {
    return {
      label: 'In progress',
      color: colorWarning,
      needsAction: false,
      reason: `${getStepLabel(active.key)} is running.`,
    };
  }

  const next = merchantSteps.find((step) => step.status === 'pending');
  return {
    label: 'Action needed',
    color: colorWarning,
    needsAction: true,
    reason: next ? `${getStepLabel(next.key)} is the next action.` : undefined,
  };
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const errors = (error as { data?: { errors?: string[] } })?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(' ');
  }

  const message = (error as { data?: { message?: string } })?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallback;
};

export const buttonPaddingSx = {
  px: 4,
  whiteSpace: 'nowrap',
  alignSelf: 'flex-start',
  flexShrink: 0,
  '& .MuiTypography-root': { whiteSpace: 'nowrap' },
} as const;
